import { Show, For, createMemo, Switch, Match } from "solid-js";
import { isEmpty, isEqual } from "lodash-es";
import Text from "../text/Text";
import DynamicValueListItem from "./listItems/DynamicValueListItem";
import type { NestedSelectOption, SelectOption } from "../../types/Menu";
import { narrow } from "../../utils/solidUtils";
import TextListItem from "./listItems/TextListItem";
import ImageListItem from "./listItems/ImageListItem";
import IconListItem from "./listItems/IconListItem";
import AvatarListItem from "./listItems/AvatarListItem";

type Group<T> = { label: string; options: SelectOption<T>[] };

type SelectMenuWithSearchOptionsProps<T> = {
  options: (SelectOption<T> | NestedSelectOption<T>)[];
  category: string;
  selectedOptions: SelectOption<T>[] | null;
  multiSelect?: boolean;
  highlightedOption: SelectOption<T> | null | undefined;
  handleOptionSelect: (option: SelectOption<T>) => void;
};

const SelectMenuWithSearchOptions = <T,>(props: SelectMenuWithSearchOptionsProps<T>) => {
  const nonNestedOptions = createMemo(() =>
    props.options.filter((field): field is SelectOption<T> => typeof field === "object" && "value" in field),
  );

  const renderGroup = (group: Group<T>, showLabel: boolean) => (
    <div role="group" aria-label={group.label}>
      <Show when={showLabel}>
        <div class="sticky top-0 flex min-w-0 bg-surface-default px-3 pt-1 pb-0.5">
          <Text text={group.label} truncate class="text-header-md text-secondary capitalize" />
        </div>
      </Show>
      <For each={group.options}>
        {(option) => {
          const htmlProps = () =>
            ({
              role: "option",
              "data-highlighted": isEqual(props.highlightedOption?.value, option.value) ? "" : undefined,
              "aria-selected": props.selectedOptions?.some((o) => isEqual(o.value, option.value)),
              tabIndex: 0,
              onClick: () => props.handleOptionSelect(option),
              onKeyDown: (e: KeyboardEvent) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  props.handleOptionSelect(option);
                }
              },
            }) as const;

          return (
            <Switch>
              <Match when={narrow(option, (o) => o.variant === "text")}>
                {(o) => <TextListItem htmlProps={htmlProps()} {...o()} />}
              </Match>
              <Match when={narrow(option, (o) => o.variant === "icon")}>
                {(o) => <IconListItem htmlProps={htmlProps()} {...o()} />}
              </Match>
              <Match when={narrow(option, (o) => o.variant === "image")}>
                {(o) => <ImageListItem htmlProps={htmlProps()} {...o()} />}
              </Match>
              <Match when={narrow(option, (o) => o.variant === "dynamicValue")}>
                {(o) => <DynamicValueListItem htmlProps={htmlProps()} {...o()} />}
              </Match>
              <Match when={narrow(option, (o) => o.variant === "avatar")}>
                {(o) => <AvatarListItem htmlProps={htmlProps()} {...o()} />}
              </Match>
            </Switch>
          );
        }}
      </For>
    </div>
  );

  const renderNestedField = (field: NestedSelectOption<T>) =>
    Object.keys(field).map((key) => {
      const options = field[key];
      if (isEmpty(options)) {
        return null;
      }
      return renderGroup({ label: key, options: field[key] }, true);
    });

  const renderField = (field: SelectOption<T> | NestedSelectOption<T>) => {
    if (typeof field === "object" && !("value" in field)) {
      return renderNestedField(field);
    } else if (Array.isArray(field)) {
      return (field as SelectOption<T>[]).map((item) => renderGroup({ label: item.label, options: [item] }, true));
    }
  };

  return (
    <div>
      {/* Always render the non-nested options at the top and in a single group */}
      {!isEmpty(nonNestedOptions()) && renderGroup({ label: props.category, options: nonNestedOptions() }, false)}
      <For each={props.options}>{(field) => renderField(field)}</For>
    </div>
  );
};

export default SelectMenuWithSearchOptions;
